/*
 * Copyright (c) 2024 metromorph softworks GmbH
 *
 * All rights reserved
 */

import { 
    App, 
    AppConfig, 
} from "@ef/common-app";

import { 
    AppScreen,
    setNotice,
    clearNotice,
    loadScreen,
    loadAppConfiguration
} from "@ef/feature-app";

// Import all features (to register reducers)
// Components
import { initLogin as initLoginAction } from "@ef/feature-login";
import { registerSmoMappers } from "@ef/feature-sync";
import "@ef/feature-onboarding";
import "@ef/components-analysis/feature/drilldown";
// APIs
import "@ef/feature-data";
import { setGqlUrl } from "@ef/feature-graphql";
import {
    registerCoreDataControllerFactory,
    CsvCoreDataController
} from "@ef/feature-print";

import { SaMainScreenLoader } from "@ef/components-sa-dashboard/ScreenSpec";
import { initLogin, initControllers } from "./config";

const config = {
    initializers: [
        async (store) => {
            store.dispatch(setNotice("Init controllers"));
            initControllers();
            registerSmoMappers();
            store.dispatch(clearNotice());
        },
        async (store) => {
            store.dispatch(setNotice("Register screens"));
            // Import screens to register routes
            // Done here (not at module level) to avoid conflicts in multi-app tests
            await import("./screens");
            store.dispatch(clearNotice());
        },
        async (store) => {
            const config = await store.dispatch(loadAppConfiguration("./appconfig.json")).unwrap();
            if (config) {
                initLogin(config);
                setGqlUrl(`${config.apiurl}/v3/graphql/query`);
            }
        },
        async (store) => {
            store.dispatch(setNotice("Init login"));
            await store.dispatch(initLoginAction());
            store.dispatch(clearNotice());
        },
        async (store) => {
            store.dispatch(setNotice("Load SaMainScreen (Default)..."));
            const screen = {
                ...( SaMainScreenLoader().screen),
                name: "USER_DEFAULT"
            } as AppScreen;
            await store.dispatch(loadScreen({
                screen,
                force: true,
                preload: true
            }));
            store.dispatch(clearNotice());
        },
        async (/* store */) => {
            // Load stuff we do not have to wait for
            // ----------------------
            // CoreDataController 
            //    Currently this is only a stub - fixed to CsvCoreDataController
            //    TODO: make configurable vi `settings-coredata`
            // Register CsvCoreDataController factory so the "Stammdaten bereitstellen"
            registerCoreDataControllerFactory(() => new CsvCoreDataController());
        }
    ],
    logStore: (typeof process != "undefined" ? 
        (process.env.LOG_REDUX as string)?.toUpperCase()
        : false
    )
} as AppConfig;

const SaApp = new App(config);

export default SaApp;
